import { useCallback, useState } from "react";
import { analytics } from "@/content";
import { Rich, RichTemplate, formatMoney } from "@/lib/rich";
import { useScrollReveal } from "@/hooks/useScrollReveal";

export default function AnalyticsCalculator() {
  const calc = analytics.calculator;
  const [revenue, setRevenue] = useState(calc.defaultValue);
  const { ref: cardRef, revealed } = useScrollReveal();

  // Модель: чем меньше оборот, тем выше доля потерь
  const lossPercent =
    calc.lossPercentBase +
    (1 - revenue / calc.max) * calc.lossPercentRange;
  const lostAmount = Math.round(revenue * (lossPercent / 100));
  const recoveredAmount = Math.round(lostAmount * calc.recoveryRate);

  const handleSliderChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRevenue(Number(event.target.value));
    },
    []
  );

  const sliderPercent =
    ((revenue - calc.min) / (calc.max - calc.min)) * 100;

  return (
    <section id="calculator" className="py-24 bg-white dark:bg-[#0A0A0A] relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[10%] right-[-5%] w-[500px] h-[500px] rounded-full bg-blue-100/30 dark:bg-blue-500/[0.10] blur-[140px]" />
        <div className="absolute bottom-[10%] left-[-5%] w-[450px] h-[450px] rounded-full bg-amber-100/25 blur-[130px]" />
      </div>

      <div className="max-w-3xl mx-auto px-6 relative z-10">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 rounded-full bg-white dark:bg-white/[0.04] border border-slate-200 dark:border-white/[0.09] text-slate-500 dark:text-white/60 text-xs font-semibold tracking-widest uppercase mb-6 shadow-sm">
            {calc.badge}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white leading-tight mb-4">
            <Rich text={calc.title} accentClassName="text-amber-600" />
          </h2>
          <p className="text-slate-600 dark:text-white/65 text-lg leading-relaxed max-w-xl mx-auto">
            {calc.subtitle}
          </p>
        </div>

        <div
          ref={cardRef}
          className={`bg-white dark:bg-white/[0.04] border border-slate-200 dark:border-white/[0.09] rounded-3xl p-8 md:p-10 shadow-md transition-all duration-700 ${
            revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          {/* Ползунок оборота */}
          <div className="mb-10">
            <div className="flex items-center justify-between mb-4 gap-4">
              <span className="text-slate-600 dark:text-white/65 text-sm font-medium">
                {calc.sliderLabel}
              </span>
              <span className="text-slate-900 dark:text-white font-extrabold text-xl whitespace-nowrap">
                {formatMoney(revenue)}
              </span>
            </div>

            <div className="relative">
              <input
                type="range"
                min={calc.min}
                max={calc.max}
                step={calc.step}
                value={revenue}
                onChange={handleSliderChange}
                aria-label={calc.sliderLabel}
                className="w-full h-2 rounded-full bg-slate-200 appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-600 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:shadow-blue-200 [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:hover:scale-110 [&::-moz-range-thumb]:w-6 [&::-moz-range-thumb]:h-6 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-blue-600 [&::-moz-range-thumb]:border-none [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:shadow-md"
                style={{
                  background: `linear-gradient(to right, #2563eb ${sliderPercent}%, #e2e8f0 ${sliderPercent}%)`,
                }}
              />
              <div className="flex justify-between mt-2">
                <span className="text-slate-500 dark:text-white/60 text-xs">{calc.minLabel}</span>
                <span className="text-slate-500 dark:text-white/60 text-xs">{calc.maxLabel}</span>
              </div>
            </div>
          </div>

          {/* Результат */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            <div className="bg-amber-50 border border-amber-100 rounded-2xl p-5 text-center">
              <div className="text-amber-700 text-xs font-semibold uppercase tracking-wide mb-1">
                {calc.lossLabel}
              </div>
              <div className="text-amber-700 font-extrabold text-3xl mb-1">
                {formatMoney(lostAmount)}/мес
              </div>
              <div className="text-amber-700 text-xs">
                <RichTemplate
                  template={calc.lossNote}
                  values={{ percent: lossPercent.toFixed(0) }}
                />
              </div>
            </div>
            <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-5 text-center">
              <div className="text-emerald-700 text-xs font-semibold uppercase tracking-wide mb-1">
                {calc.recoveredLabel}
              </div>
              <div className="text-emerald-700 font-extrabold text-3xl mb-1">
                {formatMoney(recoveredAmount)}/мес
              </div>
              <div className="text-emerald-700 text-xs">{calc.recoveredNote}</div>
            </div>
          </div>

          <div className="text-center">
            <p className="text-slate-600 dark:text-white/65 text-sm mb-4">
              <RichTemplate
                template={calc.annualText}
                values={{ annual: formatMoney(recoveredAmount * 12) }}
                accentClassName="text-slate-900 dark:text-white font-bold"
              />
            </p>
            <a
              href={calc.cta.href}
              className="inline-block px-8 py-4 rounded-xl bg-blue-600 text-white font-bold text-base hover:bg-blue-700 transition-colors cursor-pointer shadow-sm shadow-blue-200"
            >
              {calc.cta.label}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
